// Import core logic functions
import { generateTasks as generateTasksLogic, generateSoloTimeline, generatePairTimeline } from './timeline-logic.js';

// Store task configurations
let taskConfigurations = {};

// Flag to prevent infinite loops when loading from URL
let isLoadingFromURL = false;

// Animation state
let animationState = {
    isAnimating: false,
    isPaused: false,
    animationDuration: 30000, // 30 seconds
    startTime: null,
    pausedAt: 0,
    overlays: [],
    lastScrollProgress: 0,
    // Scroll animation state for dev1
    dev1: {
        isScrollAnimating: false,
        scrollStartTime: null,
        scrollStartPos: 0,
        scrollTargetPos: 0,
        scrollDuration: 1000 // 1 second for smooth scroll
    },
    // Scroll animation state for dev2
    dev2: {
        isScrollAnimating: false,
        scrollStartTime: null,
        scrollStartPos: 0,
        scrollTargetPos: 0,
        scrollDuration: 1000
    },
    // Scroll animation state for pair
    pair: {
        isScrollAnimating: false,
        scrollStartTime: null,
        scrollStartPos: 0,
        scrollTargetPos: 0,
        scrollDuration: 1000
    }
};

// Details visibility state
let detailsVisible = false;

function renderTaskConfig() {
    const numTasks = parseInt(document.getElementById('numTasks').value);
    const container = document.getElementById('task-config-list');
    container.innerHTML = '';

    for (let i = 1; i <= numTasks; i++) {
        if (!taskConfigurations[i]) {
            taskConfigurations[i] = {
                duration: 1,
                needsRework: false,
                reworkTime: 30
            };
        }

        const taskDiv = document.createElement('div');
        taskDiv.style.cssText = 'background: white; padding: 15px; border-radius: 8px; border: 2px solid #ddd;';
        taskDiv.innerHTML = `
            <div style="font-weight: 600; margin-bottom: 10px; color: #333;">Task ${i}</div>
            <div style="margin-bottom: 10px;">
                <label style="font-size: 0.9em; color: #666;">Duration: ${taskConfigurations[i].duration}h</label>
                <input type="range" id="task-${i}-duration" min="0.5" max="3" value="${taskConfigurations[i].duration}" step="0.5"
                       style="width: 100%; margin-top: 5px;">
            </div>
            <label style="display: flex; align-items: center; gap: 10px; margin-bottom: 8px; cursor: pointer;">
                <input type="checkbox" id="task-${i}-rework" ${taskConfigurations[i].needsRework ? 'checked' : ''}
                       style="width: 18px; height: 18px; cursor: pointer;">
                <span>Needs Rework</span>
            </label>
            <div style="margin-top: 10px;">
                <label style="font-size: 0.9em; color: #666;">Rework Time: ${taskConfigurations[i].reworkTime}%</label>
                <input type="range" id="task-${i}-reworktime" min="10" max="50" value="${taskConfigurations[i].reworkTime}" step="10"
                       style="width: 100%; margin-top: 5px;" ${!taskConfigurations[i].needsRework ? 'disabled' : ''}>
            </div>
        `;
        container.appendChild(taskDiv);

        // Add event listeners
        const durationSlider = taskDiv.querySelector(`#task-${i}-duration`);
        const checkbox = taskDiv.querySelector(`#task-${i}-rework`);
        const reworkSlider = taskDiv.querySelector(`#task-${i}-reworktime`);

        durationSlider.addEventListener('input', () => {
            taskConfigurations[i].duration = parseFloat(durationSlider.value);
            const label = durationSlider.previousElementSibling;
            label.textContent = `Duration: ${durationSlider.value}h`;
            updateVisualization();
        });

        checkbox.addEventListener('change', () => {
            taskConfigurations[i].needsRework = checkbox.checked;
            reworkSlider.disabled = !checkbox.checked;
            updateVisualization();
        });

        reworkSlider.addEventListener('input', () => {
            taskConfigurations[i].reworkTime = parseInt(reworkSlider.value);
            const label = reworkSlider.previousElementSibling;
            label.textContent = `Rework Time: ${reworkSlider.value}%`;
            updateVisualization();
        });
    }
}

function generateTasks() {
    const numTasks = parseInt(document.getElementById('numTasks').value);
    return generateTasksLogic(numTasks, taskConfigurations);
}

// Helper function to format decimal hours as "Xh Ym"
function formatTime(hours) {
    const h = Math.floor(hours);
    const m = Math.round((hours - h) * 60);

    if (h === 0) {
        return `${m}m`;
    } else if (m === 0) {
        return `${h}h`;
    } else {
        return `${h}h ${m}m`;
    }
}

function renderTimeline(timelineData, containerId, maxTime, actualTime) {
    const container = document.getElementById(containerId);
    container.innerHTML = '';

    // Create blocks row
    const blocksRow = document.createElement('div');
    blocksRow.style.display = 'flex';
    blocksRow.style.minHeight = '60px';

    timelineData.forEach(block => {
        const widthPercent = (block.duration / maxTime) * 100;
        const blockEl = document.createElement('div');
        blockEl.className = `time-block ${block.type}`;
        blockEl.style.width = `${widthPercent}%`;
        const timeStr = formatTime(block.duration);
        blockEl.textContent = `${block.label}\n${timeStr}`;
        blockEl.title = `${block.label}: ${timeStr}`;
        blocksRow.appendChild(blockEl);
    });

    // Add blocks to DOM first
    container.appendChild(blocksRow);

    // Now measure scrollWidth (blocks are in DOM)
    const scrollWidth = blocksRow.scrollWidth;

    // Use actualTime for hour markers (not maxTime)
    const totalHours = Math.ceil(actualTime);
    const pixelsPerHour = scrollWidth / totalHours;

    // Create time markers row
    const timeRow = document.createElement('div');
    timeRow.style.display = 'flex';
    timeRow.style.width = `${scrollWidth}px`;
    timeRow.style.marginTop = '5px';
    timeRow.style.paddingTop = '5px';
    timeRow.style.borderTop = '1px solid #ddd';

    for (let hour = 0; hour <= totalHours; hour++) {
        const marker = document.createElement('div');
        marker.style.width = `${pixelsPerHour}px`;
        marker.style.textAlign = 'center';
        marker.style.fontSize = '0.75em';
        marker.style.color = '#666';
        marker.style.flexShrink = '0';
        marker.textContent = `${hour}h`;
        timeRow.appendChild(marker);
    }

    container.appendChild(timeRow);
}

function updateVisualization() {
    const tasks = generateTasks();

    // Get config from DOM
    const config = {
        reviewTime: parseFloat(document.getElementById('reviewTime').value),
        contextSwitch: parseFloat(document.getElementById('contextSwitch').value),
        startNewTask: document.getElementById('startNewTask').checked,
        pairEfficiency: parseFloat(document.getElementById('pairEfficiency').value)
    };

    const solo = generateSoloTimeline(tasks, config);
    const pair = generatePairTimeline(tasks, config);

    // Save state to URL
    saveStateToURL();

    const maxTime = Math.max(solo.totalTime, pair.totalTime);

    // Render solo timelines
    renderTimeline(solo.dev1, 'solo-dev1', maxTime, solo.totalTime);
    renderTimeline(solo.dev2, 'solo-dev2', maxTime, solo.totalTime);

    // Synchronize scrolling between dev1 and dev2
    setupSynchronizedScrolling();

    // Render pair timeline
    renderTimeline(pair.timeline, 'pair-timeline', maxTime, pair.totalTime);

    // Update stats
    document.getElementById('solo-total-time').textContent = formatTime(solo.totalTime);
    document.getElementById('pair-total-time').textContent = formatTime(pair.totalTime);

    document.getElementById('blocked-time').textContent = formatTime(solo.waste.blocked);
    document.getElementById('review-time').textContent = formatTime(solo.waste.review);
    document.getElementById('rework-time').textContent = formatTime(solo.waste.rework);
    document.getElementById('total-waste').textContent = formatTime(solo.waste.total);

    const wastePercentage = (solo.waste.total / solo.totalTime * 100).toFixed(1);
    document.getElementById('waste-percentage').textContent = `${wastePercentage}%`;

    // Update comparison
    const wastePercentageOfSolo = (solo.waste.total / solo.totalTime * 100).toFixed(1);
    document.getElementById('waste-eliminated').textContent = formatTime(solo.waste.total);
    document.getElementById('waste-percentage-eliminated').textContent = `${wastePercentageOfSolo}%`;
    document.getElementById('blocks-eliminated').textContent = solo.waste.blockingEvents;
}

function setupControls() {
    const controls = ['numTasks', 'reviewTime', 'contextSwitch', 'pairEfficiency'];

    controls.forEach(control => {
        const input = document.getElementById(control);
        const display = document.getElementById(`${control}-value`);

        input.addEventListener('input', () => {
            let value = input.value;
            if (control === 'reviewTime' || control === 'contextSwitch') {
                // Convert hours to minutes for display
                const minutes = Math.round(parseFloat(value) * 60);
                display.textContent = minutes + 'min';
            } else if (control === 'pairEfficiency') {
                display.textContent = value + '%';
            } else {
                display.textContent = value;
            }

            // Re-render task config if numTasks changed
            if (control === 'numTasks') {
                renderTaskConfig();
            }

            updateVisualization();
        });
    });

    // Add listener for checkbox
    document.getElementById('startNewTask').addEventListener('change', () => {
        updateVisualization();
    });
}

function setupSynchronizedScrolling() {
    const dev1 = document.getElementById('solo-dev1');
    const dev2 = document.getElementById('solo-dev2');
    const pair = document.getElementById('pair-timeline');

    if (!dev1 || !dev2 || !pair) return;

    // Remove existing listeners to prevent duplicates
    dev1.onscroll = null;
    dev2.onscroll = null;
    pair.onscroll = null;

    let isScrolling = false;

    const dev1Handler = () => {
        if (!isScrolling) {
            isScrolling = true;
            dev2.scrollLeft = dev1.scrollLeft;
            pair.scrollLeft = dev1.scrollLeft;
            setTimeout(() => { isScrolling = false; }, 0);
        }
    };

    const dev2Handler = () => {
        if (!isScrolling) {
            isScrolling = true;
            dev1.scrollLeft = dev2.scrollLeft;
            pair.scrollLeft = dev2.scrollLeft;
            setTimeout(() => { isScrolling = false; }, 0);
        }
    };

    const pairHandler = () => {
        if (!isScrolling) {
            isScrolling = true;
            dev1.scrollLeft = pair.scrollLeft;
            dev2.scrollLeft = pair.scrollLeft;
            setTimeout(() => { isScrolling = false; }, 0);
        }
    };

    dev1.addEventListener('scroll', dev1Handler);
    dev2.addEventListener('scroll', dev2Handler);
    pair.addEventListener('scroll', pairHandler);

    // Store handlers for later removal
    dev1._scrollHandler = dev1Handler;
    dev2._scrollHandler = dev2Handler;
    pair._scrollHandler = pairHandler;
}

function disableSynchronizedScrolling() {
    const dev1 = document.getElementById('solo-dev1');
    const dev2 = document.getElementById('solo-dev2');
    const pair = document.getElementById('pair-timeline');

    if (dev1 && dev1._scrollHandler) {
        dev1.removeEventListener('scroll', dev1._scrollHandler);
    }
    if (dev2 && dev2._scrollHandler) {
        dev2.removeEventListener('scroll', dev2._scrollHandler);
    }
    if (pair && pair._scrollHandler) {
        pair.removeEventListener('scroll', pair._scrollHandler);
    }
}

function renderTimelineOverlay(containerId) {
    const container = document.getElementById(containerId);

    // Get the first child which is the blocks row
    const blocksRow = container.querySelector('div');
    const scrollWidth = blocksRow ? blocksRow.scrollWidth : container.scrollWidth;

    // Create overlay bar if it doesn't exist
    let overlay = container.querySelector('.timeline-overlay');
    if (!overlay) {
        overlay = document.createElement('div');
        overlay.className = 'timeline-overlay';
        overlay.style.cssText = `
            position: absolute;
            top: 0;
            left: 0;
            width: ${scrollWidth}px;
            height: 100%;
            background: #f8f9fa;
            pointer-events: none;
            z-index: 5;
        `;
        container.style.position = 'relative';
        container.appendChild(overlay);
    } else {
        // Update width if overlay already exists
        overlay.style.width = `${scrollWidth}px`;
    }

    return overlay;
}

function startAnimation() {
    if (animationState.isAnimating) {
        stopAnimation();
        // Show stats when manually stopped
        document.querySelectorAll('.scenario-stats').forEach(el => {
            el.style.display = '';
        });
        showRestoreButton();
        return;
    }

    // Show details if they're hidden (so we have timelines to animate)
    if (!detailsVisible) {
        detailsVisible = true;
        const detailSections = [
            '.scenario',
            '.legend',
            '.comparison'
        ];
        detailSections.forEach(selector => {
            document.querySelectorAll(selector).forEach(el => {
                el.style.display = '';
            });
        });

        // Update the show details button
        const showDetailsBtn = document.getElementById('show-details-btn');
        if (showDetailsBtn) {
            showDetailsBtn.textContent = '🙈 Hide Details';
        }
    }

    // Hide everything except the timelines and their labels
    const elementsToHide = [
        'h1',
        '.subtitle',
        '.controls',
        '.task-config-section',
        '.waste-summary',
        '.legend',
        '.comparison',
        '.time-axis',
        'button',
        '.preset-btn',
        '.scenario-stats' // Hide total time during animation
    ];

    elementsToHide.forEach(selector => {
        document.querySelectorAll(selector).forEach(el => {
            el.style.display = 'none';
        });
    });

    // Hide preset section text specifically
    document.querySelectorAll('body > .container > div').forEach(div => {
        const directText = Array.from(div.childNodes)
            .filter(node => node.nodeType === 3) // Text nodes
            .map(node => node.textContent.trim())
            .join('');
        if (directText.includes('Quick Presets:') || div.querySelector('.preset-btn')) {
            div.style.display = 'none';
        }
    });

    // Add spacing to bring timelines closer
    const scenarios = document.querySelectorAll('.scenario');
    scenarios.forEach(scenario => {
        scenario.style.marginBottom = '20px';
    });

    const devRows = document.querySelectorAll('.developer-row');
    devRows.forEach(row => {
        row.style.marginBottom = '10px';
    });

    const timelineContainers = document.querySelectorAll('.timeline-container');
    timelineContainers.forEach(container => {
        container.style.padding = '10px';
    });

    // Reset scroll positions to start
    const dev1 = document.getElementById('solo-dev1');
    const dev2 = document.getElementById('solo-dev2');
    const pair = document.getElementById('pair-timeline');

    [dev1, dev2, pair].forEach(timeline => {
        if (timeline) {
            timeline.scrollLeft = 0;
        }
    });

    // Disable synchronized scrolling during animation
    disableSynchronizedScrolling();

    // Render the gray overlay bars
    const dev1Overlay = renderTimelineOverlay('solo-dev1');
    const dev2Overlay = renderTimelineOverlay('solo-dev2');
    const pairOverlay = renderTimelineOverlay('pair-timeline');

    // Use 45 seconds for animation to make it easier to follow
    animationState.animationDuration = 45000;

    // Store overlays for animation
    animationState.overlays = [dev1Overlay, dev2Overlay, pairOverlay];
    animationState.isAnimating = true;
    animationState.isPaused = false;
    animationState.startTime = performance.now();
    animationState.pausedAt = 0;
    animationState.lastScrollProgress = 0;

    // Initialize overlays
    animationState.overlays.forEach(overlay => {
        overlay.style.transition = 'none';
        overlay.style.clipPath = 'inset(0 0 0 0)';
    });

    // Show pause/play button
    showPausePlayButton();

    // Start animation loop
    animateFrame();
}

function animateFrame() {
    if (!animationState.isAnimating) return;

    if (animationState.isPaused) {
        requestAnimationFrame(animateFrame);
        return;
    }

    const elapsed = performance.now() - animationState.startTime;
    const progress = Math.min(elapsed / animationState.animationDuration, 1);

    // Update overlay clip-path
    animationState.overlays.forEach(overlay => {
        const clipPercent = progress * 100;
        overlay.style.clipPath = `inset(0 0 0 ${clipPercent}%)`;
    });

    // Smart scrolling: handle each timeline independently
    const dev1 = document.getElementById('solo-dev1');
    const dev2 = document.getElementById('solo-dev2');
    const pair = document.getElementById('pair-timeline');

    // Helper function to handle scroll animation for a timeline
    const handleTimelineScroll = (timeline, timelineKey) => {
        if (!timeline) return;

        const blocksRow = timeline.querySelector('div');
        if (!blocksRow) return;

        const scrollWidth = blocksRow.scrollWidth;
        const viewportWidth = timeline.clientWidth;
        const state = animationState[timelineKey];

        // If we're animating a scroll, interpolate the position
        if (state.isScrollAnimating) {
            const scrollElapsed = performance.now() - state.scrollStartTime;
            const scrollProgress = Math.min(scrollElapsed / state.scrollDuration, 1);

            // Ease-out function for smooth deceleration
            const easeOut = 1 - Math.pow(1 - scrollProgress, 3);
            const interpolatedScroll = state.scrollStartPos +
                (state.scrollTargetPos - state.scrollStartPos) * easeOut;

            // Apply scroll
            timeline.scrollLeft = interpolatedScroll;

            // Check if scroll animation is complete
            if (scrollProgress >= 1) {
                state.isScrollAnimating = false;
            }
        } else {
            // Check if we need to start a new scroll animation
            const currentScroll = timeline.scrollLeft;
            const revealedWidth = scrollWidth * progress;
            const revealedEdgeInViewport = revealedWidth - currentScroll;

            // When revealed edge reaches 75% of viewport, scroll forward
            if (revealedEdgeInViewport > viewportWidth * 0.75) {
                // Scroll forward by 50% of viewport
                const scrollAmount = viewportWidth * 0.5;
                const newScroll = currentScroll + scrollAmount;

                // Start scroll animation
                state.isScrollAnimating = true;
                state.scrollStartTime = performance.now();
                state.scrollStartPos = currentScroll;
                state.scrollTargetPos = newScroll;
            }
        }
    };

    // Handle each timeline independently
    handleTimelineScroll(dev1, 'dev1');
    handleTimelineScroll(dev2, 'dev2');
    handleTimelineScroll(pair, 'pair');

    if (progress < 1) {
        requestAnimationFrame(animateFrame);
    } else {
        // Animation complete
        stopAnimation();
        // Show the stats after animation finishes
        document.querySelectorAll('.scenario-stats').forEach(el => {
            el.style.display = '';
        });
        showRestoreButton();
    }
}

function togglePause() {
    if (!animationState.isAnimating) return;

    animationState.isPaused = !animationState.isPaused;

    const pauseBtn = document.getElementById('pause-play-btn');
    if (pauseBtn) {
        pauseBtn.textContent = animationState.isPaused ? '▶️ Resume' : '⏸️ Pause';
    }

    if (!animationState.isPaused) {
        // Resume: adjust start time to account for pause duration
        const elapsed = animationState.pausedAt;
        animationState.startTime = performance.now() - elapsed;
    } else {
        // Pause: store current elapsed time
        animationState.pausedAt = performance.now() - animationState.startTime;
    }
}

function showPausePlayButton() {
    let pauseBtn = document.getElementById('pause-play-btn');
    if (!pauseBtn) {
        pauseBtn = document.createElement('button');
        pauseBtn.id = 'pause-play-btn';
        pauseBtn.className = 'action-btn';
        pauseBtn.textContent = '⏸️ Pause';
        pauseBtn.style.cssText = `
            position: fixed;
            top: 20px;
            left: 50%;
            transform: translateX(-50%);
            background: linear-gradient(135deg, #FF9800 0%, #F57C00 100%);
            box-shadow: 0 4px 15px rgba(255, 152, 0, 0.4);
            color: white;
            border: none;
            padding: 12px 30px;
            font-size: 1em;
            font-weight: 600;
            border-radius: 25px;
            cursor: pointer;
            transition: transform 0.2s, box-shadow 0.2s;
            z-index: 1000;
        `;
        pauseBtn.addEventListener('click', togglePause);
        document.body.appendChild(pauseBtn);
    }
    pauseBtn.style.display = 'block';
    pauseBtn.textContent = '⏸️ Pause';
}

function showRestoreButton() {
    // Create restore button if it doesn't exist
    let restoreBtn = document.getElementById('restore-view-btn');
    if (!restoreBtn) {
        restoreBtn = document.createElement('button');
        restoreBtn.id = 'restore-view-btn';
        restoreBtn.className = 'action-btn';
        restoreBtn.textContent = '🔄 Restore Full View';
        restoreBtn.style.cssText = `
            position: fixed;
            top: 20px;
            right: 20px;
            background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
            box-shadow: 0 4px 15px rgba(102, 126, 234, 0.4);
            color: white;
            border: none;
            padding: 12px 30px;
            font-size: 1em;
            font-weight: 600;
            border-radius: 25px;
            cursor: pointer;
            transition: transform 0.2s, box-shadow 0.2s;
            z-index: 1000;
        `;
        restoreBtn.addEventListener('click', restoreView);
        document.body.appendChild(restoreBtn);
    }
    restoreBtn.style.display = 'block';
}

function stopAnimation() {
    animationState.isAnimating = false;
    animationState.isPaused = false;

    // Reset scroll animation states
    animationState.dev1.isScrollAnimating = false;
    animationState.dev2.isScrollAnimating = false;
    animationState.pair.isScrollAnimating = false;

    // Hide pause/play button
    const pauseBtn = document.getElementById('pause-play-btn');
    if (pauseBtn) {
        pauseBtn.style.display = 'none';
    }

    // Remove all overlays
    document.querySelectorAll('.timeline-overlay').forEach(overlay => {
        overlay.remove();
    });

    // Re-enable synchronized scrolling
    setupSynchronizedScrolling();
}

function restoreView() {
    // Reset animation state
    animationState.isAnimating = false;
    animationState.isPaused = false;

    // Remove any overlays
    document.querySelectorAll('.timeline-overlay').forEach(overlay => {
        overlay.remove();
    });

    // Reset scroll positions
    const dev1 = document.getElementById('solo-dev1');
    const dev2 = document.getElementById('solo-dev2');
    const pair = document.getElementById('pair-timeline');

    [dev1, dev2, pair].forEach(timeline => {
        if (timeline) {
            timeline.scrollLeft = 0;
        }
    });

    // Restore all hidden elements
    const elementsToRestore = [
        'h1',
        '.subtitle',
        '.controls',
        '.task-config-section',
        '.waste-summary',
        '.legend',
        '.comparison',
        '.time-axis',
        'button',
        '.preset-btn',
        '.scenario-stats'
    ];

    elementsToRestore.forEach(selector => {
        document.querySelectorAll(selector).forEach(el => {
            el.style.display = '';
        });
    });

    // Hide restore button and pause/play button AFTER restoring everything
    const restoreBtn = document.getElementById('restore-view-btn');
    if (restoreBtn) {
        restoreBtn.style.display = 'none';
    }

    const pauseBtn = document.getElementById('pause-play-btn');
    if (pauseBtn) {
        pauseBtn.style.display = 'none';
    }

    // Restore preset section
    document.querySelectorAll('body > .container > div').forEach(div => {
        const directText = Array.from(div.childNodes)
            .filter(node => node.nodeType === 3)
            .map(node => node.textContent.trim())
            .join('');
        if (directText.includes('Quick Presets:') || div.querySelector('.preset-btn')) {
            div.style.display = '';
        }
    });

    // Restore spacing
    const scenarios = document.querySelectorAll('.scenario');
    scenarios.forEach(scenario => {
        scenario.style.marginBottom = '';
    });

    const devRows = document.querySelectorAll('.developer-row');
    devRows.forEach(row => {
        row.style.marginBottom = '';
    });

    const timelineContainers = document.querySelectorAll('.timeline-container');
    timelineContainers.forEach(container => {
        container.style.padding = '';
    });
}


function saveStateToURL() {
    if (isLoadingFromURL) return; // Prevent loops

    const params = new URLSearchParams();

    // Save global settings
    params.set('numTasks', document.getElementById('numTasks').value);
    params.set('reviewTime', document.getElementById('reviewTime').value);
    params.set('contextSwitch', document.getElementById('contextSwitch').value);
    params.set('pairEfficiency', document.getElementById('pairEfficiency').value);
    params.set('startNewTask', document.getElementById('startNewTask').checked ? '1' : '0');

    // Save task configurations (compact format)
    const numTasks = parseInt(document.getElementById('numTasks').value);
    const taskData = [];
    for (let i = 1; i <= numTasks; i++) {
        if (taskConfigurations[i]) {
            const cfg = taskConfigurations[i];
            // Format: duration,needsRework(1/0),reworkTime
            taskData.push(`${cfg.duration},${cfg.needsRework ? 1 : 0},${cfg.reworkTime}`);
        }
    }
    if (taskData.length > 0) {
        params.set('tasks', taskData.join('|'));
    }

    // Update URL without reloading page
    const newURL = window.location.pathname + '?' + params.toString();
    window.history.replaceState({}, '', newURL);
}

function loadStateFromURL() {
    const params = new URLSearchParams(window.location.search);

    if (params.toString() === '') return; // No params, use defaults

    isLoadingFromURL = true;

    // Load global settings
    if (params.has('numTasks')) {
        document.getElementById('numTasks').value = params.get('numTasks');
        document.getElementById('numTasks-value').textContent = params.get('numTasks');
    }

    if (params.has('reviewTime')) {
        const value = params.get('reviewTime');
        document.getElementById('reviewTime').value = value;
        const minutes = Math.round(parseFloat(value) * 60);
        document.getElementById('reviewTime-value').textContent = minutes + 'min';
    }

    if (params.has('contextSwitch')) {
        const value = params.get('contextSwitch');
        document.getElementById('contextSwitch').value = value;
        const minutes = Math.round(parseFloat(value) * 60);
        document.getElementById('contextSwitch-value').textContent = minutes + 'min';
    }

    if (params.has('pairEfficiency')) {
        document.getElementById('pairEfficiency').value = params.get('pairEfficiency');
        document.getElementById('pairEfficiency-value').textContent = params.get('pairEfficiency') + '%';
    }

    if (params.has('startNewTask')) {
        document.getElementById('startNewTask').checked = params.get('startNewTask') === '1';
    }

    // Load task configurations
    if (params.has('tasks')) {
        const taskData = params.get('tasks').split('|');
        taskData.forEach((data, index) => {
            const [duration, needsRework, reworkTime] = data.split(',');
            taskConfigurations[index + 1] = {
                duration: parseFloat(duration),
                needsRework: needsRework === '1',
                reworkTime: parseInt(reworkTime)
            };
        });
    }

    isLoadingFromURL = false;
}

function loadPreset(presetName) {
    isLoadingFromURL = true; // Prevent URL updates during load

    // Reset task configurations
    taskConfigurations = {};

    if (presetName === 'clean') {
        // Clean Code: 10 tasks, varied durations, no rework
        document.getElementById('numTasks').value = 10;
        document.getElementById('numTasks-value').textContent = '10';

        const cleanDurations = [0.5, 1, 1.5, 1, 2, 1, 1.5, 0.5, 1, 1.5];
        for (let i = 1; i <= 10; i++) {
            taskConfigurations[i] = {
                duration: cleanDurations[i - 1],
                needsRework: false,
                reworkTime: 30
            };
        }
    } else if (presetName === 'realistic') {
        // Realistic Project: 100 tasks, varied durations, 30% have rework
        document.getElementById('numTasks').value = 100;
        document.getElementById('numTasks-value').textContent = '100';

        const realisticDurations = [1, 1.5, 0.5, 2, 1, 1.5, 1, 0.5, 1.5, 2];
        for (let i = 1; i <= 100; i++) {
            taskConfigurations[i] = {
                duration: realisticDurations[(i - 1) % 10], // Cycle through the pattern
                needsRework: i % 3 === 0, // Every 3rd task needs rework
                reworkTime: 30
            };
        }
    } else if (presetName === 'high-churn') {
        // High Churn: 100 tasks, varied durations, 50% have rework
        document.getElementById('numTasks').value = 100;
        document.getElementById('numTasks-value').textContent = '100';

        const churnDurations = [2, 1, 1.5, 1, 2, 0.5, 1.5, 1, 2, 1.5];
        for (let i = 1; i <= 100; i++) {
            taskConfigurations[i] = {
                duration: churnDurations[(i - 1) % 10], // Cycle through the pattern
                needsRework: i % 2 === 0, // Every other task needs rework
                reworkTime: 30
            };
        }
    }

    // Reset other settings to defaults
    document.getElementById('reviewTime').value = 0.25;
    document.getElementById('reviewTime-value').textContent = '15min';
    document.getElementById('contextSwitch').value = 0.25;
    document.getElementById('contextSwitch-value').textContent = '15min';
    document.getElementById('pairEfficiency').value = 30;
    document.getElementById('pairEfficiency-value').textContent = '30%';
    document.getElementById('startNewTask').checked = false;

    isLoadingFromURL = false;

    // Re-render and update
    renderTaskConfig();
    updateVisualization();
}

function toggleDetails() {
    detailsVisible = !detailsVisible;

    const detailSections = [
        '.scenario',
        '.legend',
        '.comparison'
    ];

    detailSections.forEach(selector => {
        document.querySelectorAll(selector).forEach(el => {
            el.style.display = detailsVisible ? '' : 'none';
        });
    });

    const btn = document.getElementById('show-details-btn');
    if (btn) {
        btn.textContent = detailsVisible ? '🙈 Hide Details' : '👁️ Show Details';
    }
}

window.addEventListener('load', () => {
    // Load state from URL first (before rendering)
    loadStateFromURL();

    // Set dynamic copyright year
    const copyrightYear = document.getElementById('copyright-year');
    if (copyrightYear) {
        copyrightYear.textContent = new Date().getFullYear();
    }

    setupControls();
    renderTaskConfig();
    updateVisualization();

    // Hide details by default
    const detailSections = [
        '.scenario',
        '.legend',
        '.comparison'
    ];
    detailSections.forEach(selector => {
        document.querySelectorAll(selector).forEach(el => {
            el.style.display = 'none';
        });
    });

    // Wire up copy link button
    const copyLinkBtn = document.getElementById('copy-link-btn');
    if (copyLinkBtn) {
        copyLinkBtn.addEventListener('click', async () => {
            try {
                await navigator.clipboard.writeText(window.location.href);
                // Show feedback
                const originalText = copyLinkBtn.textContent;
                copyLinkBtn.textContent = '✅ Link Copied!';
                setTimeout(() => {
                    copyLinkBtn.textContent = originalText;
                }, 2000);
            } catch (err) {
                // Fallback for browsers that don't support clipboard API
                alert('Copy this link:\n\n' + window.location.href);
            }
        });
    }

    // Wire up preset buttons
    const presetBtns = document.querySelectorAll('.preset-btn');
    presetBtns.forEach(btn => {
        btn.addEventListener('click', () => {
            const preset = btn.getAttribute('data-preset');
            loadPreset(preset);
        });
    });

    // Wire up animate button
    const animateBtn = document.getElementById('animate-btn');
    if (animateBtn) {
        animateBtn.addEventListener('click', () => {
            startAnimation();
        });
    }

    // Wire up show details button
    const showDetailsBtn = document.getElementById('show-details-btn');
    if (showDetailsBtn) {
        // Set initial button text
        showDetailsBtn.textContent = '👁️ Show Details';
        showDetailsBtn.addEventListener('click', () => {
            toggleDetails();
        });
    }
});
